import React, { useCallback, useEffect, useRef, useState } from 'react';
import { fetchShopPlaceDetails, lookupShopPlaces } from '../../utils/shopAuthApi';

/**
 * Modal for an owner to find their shop on Google and pull its details in.
 *
 * A modal rather than fields inline in the signup form: the form is already two screens
 * long, and results need room for a photo, the full address and a rating — without those
 * an owner cannot tell two shops of the same name apart, which is the whole point of
 * searching rather than typing.
 *
 * The Google place id is never shown. Owners do not know what one is; they recognise
 * their shop by its photo and street, and the id rides along behind the selection.
 */

function StarIcon() {
    return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <polygon points="12 2 15 8.5 22 9.2 16.8 13.8 18.3 21 12 17.4 5.7 21 7.2 13.8 2 9.2 9 8.5 12 2" />
        </svg>
    );
}

function ShopIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 9 5 3h14l2 6" /><path d="M4 9h16v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" /><path d="M9 22V12h6v10" />
        </svg>
    );
}

function ResultCard({ suggestion, busy, onPick }) {
    const closed = suggestion.business_status === 'CLOSED_PERMANENTLY';
    return (
        <button
            type="button"
            disabled={busy}
            onClick={() => onPick(suggestion)}
            className="flex w-full items-start gap-3 rounded-xl border border-[#e2d7ca] bg-white p-3 text-left transition hover:border-[#414141] hover:bg-[#fafafa] focus:border-[#D81F26] focus:outline-none focus:ring-4 focus:ring-[#D81F26]/10 disabled:cursor-wait disabled:opacity-60"
        >
            {suggestion.photo_url ? (
                <img
                    src={suggestion.photo_url}
                    alt=""
                    loading="lazy"
                    className="h-16 w-16 shrink-0 rounded-lg object-cover"
                />
            ) : (
                <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-[#f2f2f2] text-[#868686]">
                    <ShopIcon />
                </span>
            )}
            <span className="min-w-0 flex-1">
                <span className="block text-[14px] font-black leading-snug text-[#414141]">{suggestion.name}</span>
                <span className="mt-0.5 block text-[12px] font-semibold leading-relaxed text-[#868686]">
                    {suggestion.address}
                </span>
                <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                    {suggestion.rating ? (
                        <span className="inline-flex items-center gap-1 text-[12px] font-bold text-[#c07c00]">
                            <StarIcon />
                            {suggestion.rating}
                            {suggestion.review_count ? (
                                <span className="font-semibold text-[#a3a3a3]">({suggestion.review_count})</span>
                            ) : null}
                        </span>
                    ) : null}
                    {suggestion.primary_type ? (
                        <span className="text-[11px] font-bold text-[#868686]">{suggestion.primary_type}</span>
                    ) : null}
                    {closed ? (
                        <span className="text-[11px] font-black text-[#b42318]">Google lists this as closed</span>
                    ) : null}
                </span>
            </span>
        </button>
    );
}

export default function GoogleShopPicker({ open, onClose, onSelect, initialQuery = '' }) {
    const [query, setQuery] = useState(initialQuery);
    const [suggestions, setSuggestions] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [pickingId, setPickingId] = useState('');
    const [message, setMessage] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    const abortRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        setQuery(initialQuery);
        setSuggestions([]);
        setMessage('');
        setHasSearched(false);
        setPickingId('');
        inputRef.current?.focus();
    }, [open, initialQuery]);

    // Close on Escape so the modal behaves like every other dialog the owner has used.
    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    const runSearch = useCallback(async (text) => {
        const trimmed = text.trim();
        if (trimmed.length < 3) {
            setSuggestions([]);
            setHasSearched(false);
            return;
        }
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setIsSearching(true);
        setMessage('');
        try {
            const results = await lookupShopPlaces({ input: trimmed, signal: controller.signal });
            if (controller.signal.aborted) return;
            setSuggestions(results);
            setHasSearched(true);
        } catch (err) {
            if (err?.name === 'AbortError') return;
            setSuggestions([]);
            setHasSearched(true);
            setMessage(err?.message || 'Could not reach Google. Close this and type the address instead.');
        } finally {
            if (!controller.signal.aborted) setIsSearching(false);
        }
    }, []);

    // Debounced so a shop name typed at speed costs one search, not one per letter.
    useEffect(() => {
        if (!open) return undefined;
        const timer = setTimeout(() => runSearch(query), 400);
        return () => clearTimeout(timer);
    }, [open, query, runSearch]);

    useEffect(() => () => abortRef.current?.abort(), []);

    async function handlePick(suggestion) {
        setPickingId(suggestion.place_id);
        setMessage('');
        try {
            const place = await fetchShopPlaceDetails({ placeId: suggestion.place_id });
            if (place) {
                onSelect(place);
                onClose();
            } else {
                setMessage('That listing had no address details. Pick another or enter your details manually.');
            }
        } catch (err) {
            setMessage(err?.message || 'Could not load that shop. Pick another or type the address in.');
        } finally {
            setPickingId('');
        }
    }

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[120] flex items-end justify-center sm:items-center">
            <button
                type="button"
                aria-label="Close shop search"
                onClick={onClose}
                className="absolute inset-0 bg-[#414141]/50"
            />
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="google-shop-picker-title"
                className="relative flex max-h-[86vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-[#e2d7ca] bg-[#fbfaf8] shadow-2xl sm:rounded-2xl"
            >
                <div className="flex items-start justify-between gap-3 border-b border-[#efe8e0] bg-white px-5 py-4">
                    <div className="min-w-0">
                        <h2 id="google-shop-picker-title" className="text-[17px] font-black text-[#414141]">
                            Find your shop on Google
                        </h2>
                        <p className="mt-0.5 text-[12px] font-semibold text-[#868686]">
                            Pick your listing and we'll fill in the address and hours.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="shrink-0 rounded-lg px-2 py-1 text-[20px] font-black leading-none text-[#868686] transition hover:bg-[#F9F9F9] hover:text-[#414141]"
                    >
                        ×
                    </button>
                </div>

                <div className="border-b border-[#efe8e0] bg-white px-5 pb-4">
                    <label className="sr-only" htmlFor="google-shop-picker-input">Shop name</label>
                    <input
                        ref={inputRef}
                        id="google-shop-picker-input"
                        type="text"
                        autoComplete="off"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Shop name, with city or ZIP"
                        className="h-12 w-full rounded-xl border border-[#d8cbbd] bg-[#fbfaf8] px-4 text-[15px] font-semibold text-[#414141] outline-none transition placeholder:font-medium placeholder:text-[#a9a9a9] focus:border-[#D81F26] focus:ring-4 focus:ring-[#D81F26]/10"
                    />
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4">
                    {message ? (
                        <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] font-bold text-red-700">
                            {message}
                        </p>
                    ) : null}

                    {isSearching && suggestions.length === 0 ? (
                        <div className="flex flex-col gap-2.5">
                            {[1, 2, 3].map((item) => (
                                <div key={item} className="h-[88px] animate-pulse rounded-xl bg-white" />
                            ))}
                        </div>
                    ) : null}

                    {suggestions.length > 0 ? (
                        <div className="flex flex-col gap-2.5">
                            {suggestions.map((suggestion) => (
                                <ResultCard
                                    key={suggestion.place_id}
                                    suggestion={suggestion}
                                    busy={Boolean(pickingId)}
                                    onPick={handlePick}
                                />
                            ))}
                        </div>
                    ) : null}

                    {!isSearching && !suggestions.length && !message ? (
                        <p className="px-1 py-6 text-center text-[13px] font-semibold text-[#868686]">
                            {hasSearched
                                ? 'No matching repair shops found. Try adding your city or ZIP, or close this and enter your details manually.'
                                : 'Start typing your shop name to search Google.'}
                        </p>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
